import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Fade, Typography, IconButton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Edit, Delete } from "@mui/icons-material";
import clsx from "clsx";
import DraggableList from "../DraggableList/DraggableList";
import CourseActivityIcon from "../Icons/CourseActivityIcon";
import { useTranslation } from "react-i18next";
import WarningIcon from "@mui/icons-material/Warning";
import ToolTip from "../Tooltip/Tooltip";
import WarningModal from "../WarningModal/WarningModal";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    padding: theme.spacing(2.5, 5, 0, 7),
    justifyContent: "center"
  },
  isDragging: {
    ...theme.mixins.draggableRow
  },
  contentContainer: {
    borderBottom: ({ hasBorder }) =>
      hasBorder ? `solid 1px ${theme.palette.divider}` : "none",
    paddingBottom: theme.spacing(2.5)
  },
  description: {
    wordBreak: "break-word",
    ...theme.typography.body2
  }
}));

const ShortCourseDragHandle = ({ dragHandle, show }) => (
  <Fade in={show}>
    <Box position="absolute" left={18}>
      {dragHandle}
    </Box>
  </Fade>
);

const ShortCourseActivity = ({
  activity,
  dragHandle,
  isDragging,
  isDraggingOver,
  hasDivider,
  onEditActivityClick,
  onDeleteActivity,
  isOverview,
  canManageContentForPolicy,
  isPolicy
}) => {
  const classes = useStyles({ hasBorder: !isDragging && hasDivider });
  const [hoverOverActivity, setHoverOverActivity] = useState(false);
  const [
    editActivityWarningModalOpen,
    setEditActivityWarningModalOpen
  ] = useState(false);
  const handleHoverOverActivity = hovering => {
    setHoverOverActivity(hovering);
  };

  const editGraceHillActivity = () => {
    setEditActivityWarningModalOpen(true);
  };

  const onSubmitWarningModal = activity => {
    setEditActivityWarningModalOpen(false);
    onEditActivityClick(activity);
  };

  const canManageContent = () => {
    return !isPolicy || canManageContentForPolicy;
  };

  const { t } = useTranslation();

  return (
    <div
      data-qa="course-builder-short-course-activity"
      className={clsx(classes.root, { [classes.isDragging]: isDragging })}
      onMouseEnter={() => handleHoverOverActivity(true)}
      onMouseLeave={() => handleHoverOverActivity(false)}
    >
      <Box display="flex">
        <ShortCourseDragHandle
          dragHandle={dragHandle}
          show={hoverOverActivity && !isDraggingOver && canManageContent()}
        />
        <Box
          pr={4}
          pb={2.5}
          display="flex"
          justifyContent="space-between"
          width={56}
        >
          <CourseActivityIcon type={activity.contentableType} color="action" />
        </Box>
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          className={classes.contentContainer}
        >
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1">{activity.title}</Typography>
              {activity.processing && (
                <ToolTip
                  title={
                    <Typography color="textPrimary">
                      {t("videoProcessing.view")}
                    </Typography>
                  }
                >
                  <Box ml={2}>
                    <WarningIcon color="primary" />
                  </Box>
                </ToolTip>
              )}
            </Box>
            {hoverOverActivity && !isOverview && canManageContent() && (
              <Box
                display="flex"
                alignItems="center"
                position="relative"
                width={88}
              >
                {(activity.editable || isPolicy) && (
                  <Box position="absolute">
                    <IconButton
                      onClick={() =>
                        activity.editable
                          ? onEditActivityClick(activity)
                          : editGraceHillActivity()
                      }
                      data-qa="editable-title-edit-btn"
                      size="large"
                    >
                      <Edit color="primary" />
                    </IconButton>
                  </Box>
                )}
                <Box position="absolute" right={0}>
                  <IconButton
                    onClick={() => onDeleteActivity(activity.id)}
                    size="large"
                  >
                    <Delete color="primary" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
          <WarningModal
            open={editActivityWarningModalOpen}
            warningMessage={t("policyBuilder.editActivityWarningMessage")}
            onClose={() => setEditActivityWarningModalOpen(false)}
            onSubmit={() => onSubmitWarningModal(activity)}
          />
          {activity.contentableType !== "homework" &&
            !!activity.description && (
              <Box
                className={classes.description}
                flex={1}
                display="flex"
                justifyContent="space-between"
                pt={2.25}
                pb={0.75}
                dangerouslySetInnerHTML={{ __html: activity.description }}
              />
            )}
        </Box>
      </Box>
    </div>
  );
};

const ShortCourseActivities = ({
  activities,
  onReorderActivities,
  isOverview,
  onEditActivity,
  onDeleteActivity,
  canManageContentForPolicy,
  isPolicy
}) => {
  return activities ? (
    isOverview ? (
      activities.map((activity, index) => (
        <ShortCourseActivity
          activity={activity}
          key={activity.id}
          hasDivider={index !== activities.length - 1}
          onDeleteActivity={onDeleteActivity}
          onEditActivityClick={onEditActivity}
          isOverview={isOverview}
        />
      ))
    ) : (
      <DraggableList
        onDragEnd={onReorderActivities}
        droppableId="short-course-activities"
        items={activities.map((activity, index) => ({
          render: ({ dragHandle, isDragging, isDraggingOver }) => (
            <ShortCourseActivity
              activity={activity}
              key={activity.id}
              dragHandle={dragHandle}
              isDragging={isDragging}
              isDraggingOver={isDraggingOver}
              hasDivider={index !== activities.length - 1}
              onDeleteActivity={onDeleteActivity}
              onEditActivityClick={onEditActivity}
              canManageContentForPolicy={canManageContentForPolicy}
              isPolicy={isPolicy}
            />
          ),
          draggableId: activity.id
        }))}
      />
    )
  ) : null;
};

export default ShortCourseActivities;

ShortCourseActivities.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      // "shortCourseActivity"
      type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      contentableType: PropTypes.string,
      contentableId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      position: PropTypes.number
    })
  ),
  onDeleteActivity: PropTypes.func,
  /**
   * Called when the activities are reordered
   */
  onReorderActivities: PropTypes.func,
  isOverview: PropTypes.bool
};

ShortCourseActivities.defaultProps = {
  activities: []
};
